/**
 * Pipeline trigger registry — hand-transcribed from the C# data-plane
 * (tipranks-dotnet-apps) and must stay in sync with it:
 *
 * - DATA_TRIGGERS mirrors the HTTP triggers of the TriggerManager function app
 *   (TipRanks.TriggerManager.Function/Functions/*.cs) and the bulk triggers of
 *   the Stock-ETL function app (TipRanks.ETL.Function/Functions/*.cs).
 * - CHECKPOINT_LABELS mirrors TriggerManagerStorageModelKeys
 *   (TipRanks.Data.Storage/Models/TriggerManagerStorageModel.cs).
 *
 * The API validates every incoming key against these registries — the client
 * never sends URLs, only registry keys.
 */

/**
 * Outcome of one upstream trigger call. "started" means the call had not
 * settled inside its window and was left running detached — not a failure.
 */
export type RunTriggerStatus = "completed" | "started" | "failed";

export type DataTriggerApp = "triggerManager" | "stockEtl";

/**
 * fast → the admin API waits up to 20s for the upstream result.
 * slow → the admin API waits 3s, then detaches and reports "started"
 *        (the upstream ETL app has no function timeout — bulk jobs can run
 *        for many minutes).
 */
export type DataTriggerDuration = "fast" | "slow";

export interface DataTriggerDef {
  key: string;
  label: string;
  description: string;
  app: DataTriggerApp;
  /** Function route segment appended to the app's /api base. */
  path: string;
  duration: DataTriggerDuration;
  /** Key in trigger-manager.json this scan advances (TriggerManager only). */
  checkpointKey?: string;
}

export const DATA_TRIGGERS = [
  // ── TriggerManager delta scans: read SQL change tracking since the last
  //    checkpoint, enqueue changed IDs to Service Bus, commit checkpoint. ──
  { key: "ai-analyst-report", label: "AI Analyst Report", description: "Scan for changed AI analyst reports", app: "triggerManager", path: "AiAnalystReportHttpTrigger", duration: "fast", checkpointKey: "aianalystreport" },
  { key: "ai-factors", label: "AI Factors", description: "Scan for changed AI factors", app: "triggerManager", path: "AiFactorsHttp", duration: "fast", checkpointKey: "aifactors" },
  { key: "ai-factors-summary", label: "AI Factors Summary", description: "Scan for changed AI factor summaries", app: "triggerManager", path: "AiFactorsSummaryHttp", duration: "fast", checkpointKey: "aifactorssummary" },
  { key: "bull-bear-summary", label: "Bull/Bear Summary", description: "Scan for changed bull/bear summaries", app: "triggerManager", path: "BullBearSummaryHttp", duration: "fast", checkpointKey: "bullbearsummary" },
  { key: "buybacks", label: "Buybacks", description: "Scan for changed buyback data", app: "triggerManager", path: "buyBacksHttp", duration: "fast", checkpointKey: "buybacks" },
  { key: "clinical-trials", label: "Clinical Trials", description: "Scan for changed clinical trials", app: "triggerManager", path: "ClinicalTrialsHttp", duration: "fast", checkpointKey: "clinicaltrials" },
  { key: "dividends", label: "Dividends", description: "Scan for changed dividend data", app: "triggerManager", path: "dividendsHttp", duration: "fast", checkpointKey: "dividends" },
  { key: "earnings", label: "Earnings", description: "Scan for changed earnings data", app: "triggerManager", path: "earningsHttp", duration: "fast", checkpointKey: "earnings" },
  { key: "expert-operation", label: "Expert Operations", description: "Scan for changed expert operations (ratings, transactions)", app: "triggerManager", path: "ExpertOperationHttp", duration: "fast", checkpointKey: "expertoperation" },
  { key: "expert-portfolio-operation", label: "Expert Portfolio Operations", description: "Scan for changed expert portfolio operations", app: "triggerManager", path: "ExpertPortfolioOperationHttp", duration: "fast", checkpointKey: "expertportfoliooperation" },
  { key: "financials", label: "Financials", description: "Scan for changed financial statements", app: "triggerManager", path: "financialsHttp", duration: "fast", checkpointKey: "financials" },
  { key: "insider-operation", label: "Insider Operations", description: "Scan for changed insider operations", app: "triggerManager", path: "InsiderOperationHttp", duration: "fast", checkpointKey: "insideroperation" },
  { key: "key-points", label: "News Key Points", description: "Scan for changed news key points", app: "triggerManager", path: "KeyPointsHttp", duration: "fast", checkpointKey: "keypoints" },
  { key: "kpis", label: "Company KPIs", description: "Scan for changed company KPIs", app: "triggerManager", path: "kpisHttp", duration: "fast", checkpointKey: "kpis" },
  { key: "news-items", label: "News Items", description: "Scan for changed news items", app: "triggerManager", path: "NewsItemHttp", duration: "fast", checkpointKey: "newsitems" },
  { key: "stock-annual-revenue", label: "Annual Revenue", description: "Scan for changed annual revenue data", app: "triggerManager", path: "StockAnnualRevenueHttpTrigger", duration: "fast", checkpointKey: "stockannualrevenue" },
  { key: "stock-data", label: "Stock Data", description: "Scan for changed core stock data", app: "triggerManager", path: "StockDataHttp", duration: "fast", checkpointKey: "stockdata" },
  { key: "stock-listings", label: "Stock Listings", description: "Scan for changed stock listings", app: "triggerManager", path: "StockListingHttpTrigger", duration: "fast", checkpointKey: "stocklistings" },
  { key: "stock-peers", label: "Stock Peers", description: "Scan for changed stock peers", app: "triggerManager", path: "StockPeersHttpTrigger", duration: "fast", checkpointKey: "stockpeers" },
  { key: "user-listing-subscribe", label: "User Listing Subscriptions", description: "Scan for changed user listing subscriptions", app: "triggerManager", path: "UserListingSubscribeHttpTrigger", duration: "fast", checkpointKey: "userlistingsubscribe" },
  { key: "users", label: "Users", description: "Scan for changed users", app: "triggerManager", path: "UsersHttpTrigger", duration: "fast", checkpointKey: "users" },
  // ── Stock-ETL bulk triggers: recompute across all stocks. ──
  { key: "scores-all", label: "Recompute All Scores", description: "Recompute scores for every stock", app: "stockEtl", path: "ScoresAllHttp", duration: "slow" },
  { key: "smart-score-all", label: "Recompute All Smart Scores", description: "Recompute the Smart Score for every stock", app: "stockEtl", path: "SmartScoreAllHttp", duration: "slow" },
  { key: "sector-scores-all", label: "Recompute Sector Scores", description: "Recompute all sector scores", app: "stockEtl", path: "SectorScoresAllHttp", duration: "slow" },
  { key: "news-sentiment-all", label: "News Sentiment Sweep", description: "Recompute news sentiment for every stock", app: "stockEtl", path: "NewsSentimentHttp", duration: "slow" },
  { key: "currency-exchange-rate", label: "Refresh FX Rates", description: "Refresh currency exchange rates", app: "stockEtl", path: "CurrencyExchangeRateHttp", duration: "fast" },
  { key: "short-interest-enqueue", label: "Enqueue Short Interest", description: "Build and enqueue short-interest batches", app: "stockEtl", path: "EnqueueStockShortInterestHttp", duration: "fast" },
  { key: "translations-sync-all", label: "Sync All Hebrew Translations", description: "Sync all Hebrew asset translations to Mongo", app: "stockEtl", path: "translations-sync-all", duration: "slow" },
] as const satisfies readonly DataTriggerDef[];

export type DataTriggerKey = (typeof DATA_TRIGGERS)[number]["key"];

/**
 * All 25 checkpoint keys that appear in trigger-manager.json, including the
 * four with no runnable trigger from here: `segments` / `transcripts` need a
 * function key (POST), and `ownership` / `riskfactors` have no HTTP trigger.
 */
export const CHECKPOINT_LABELS: Record<string, string> = {
  aianalystreport: "AI Analyst Report",
  aifactors: "AI Factors",
  aifactorssummary: "AI Factors Summary",
  bullbearsummary: "Bull/Bear Summary",
  buybacks: "Buybacks",
  clinicaltrials: "Clinical Trials",
  dividends: "Dividends",
  earnings: "Earnings",
  expertoperation: "Expert Operations",
  expertportfoliooperation: "Expert Portfolio Operations",
  financials: "Financials",
  // Present in the blob but absent from TriggerManagerStorageModelKeys.
  fundamentals: "Fundamentals",
  insideroperation: "Insider Operations",
  keypoints: "News Key Points",
  kpis: "Company KPIs",
  newsitems: "News Items",
  ownership: "Ownership",
  riskfactors: "Risk Factors",
  segments: "Earnings Segments",
  stockannualrevenue: "Annual Revenue",
  stockdata: "Stock Data",
  stocklistings: "Stock Listings",
  stockpeers: "Stock Peers",
  transcripts: "Earnings Transcripts",
  userlistingsubscribe: "User Listing Subscriptions",
  users: "Users",
};

export function getDataTrigger(key: string): DataTriggerDef | undefined {
  return DATA_TRIGGERS.find((t) => t.key === key);
}
